import React from 'react';
import PropTypes from 'prop-types';
import { FormControl } from './Controls/FormControl';
import FormGroup from 'react-bootstrap/FormGroup';
import FormLabel from 'react-bootstrap/FormLabel';
import { InfoTooltip } from '../InfoTooltip';
import { UnderscoresToTitleCase } from '../../utils/StringUtil';
import { GetValidationPattern, GetValidationMessage } from '../../utils/ValidationUtil';
import { i18n } from '../../i18n/translate';
import { kebabCase } from 'lodash';
import { formatClassName } from '../../utils';

export class FormField extends React.Component {
  static propTypes = {
    activeLanguage: PropTypes.string,
    caption: PropTypes.string,
    children: PropTypes.element,
    className: PropTypes.string,
    data: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      type: PropTypes.string,
    }),
    dataObjectAction: PropTypes.string,
    dataObjectAttribute: PropTypes.string,
    dataObjectId: PropTypes.string,
    dataObjectType: PropTypes.string,
    dataValue: PropTypes.any,
    disabled: PropTypes.bool,
    errorColor: PropTypes.string,
    errorMessage: PropTypes.string,
    hintLink: PropTypes.node,
    hintText: PropTypes.string,
    hideControlLabelAria: PropTypes.bool,
    inputClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    isFloatOutside: PropTypes.bool,
    languages: PropTypes.arrayOf(PropTypes.object),
    multilingual: PropTypes.bool,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onValueChange: PropTypes.func,
    pattern: PropTypes.string,
    required: PropTypes.bool,
    runValidation: PropTypes.bool,
    scrollContainerId: PropTypes.string,
    searchField: PropTypes.element,
    srHelpText: PropTypes.string,
    tooltip: PropTypes.string,
    tooltipPosition: PropTypes.string,
    validationType: PropTypes.string,
    valueChangeDelay: PropTypes.number,
    wrapperClassName: PropTypes.string,
  };

  static defaultProps = {
    errorColor: '#e71c0d',
    tooltipPosition: 'left',
  };

  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      validationError: false,
    };
  }

  setError = (error, message) => {
    if (this.state.hasError !== error) {
      this.setState({ hasError: error }, () => {
        if (this.props.validationType && this.state.hasError) {
          this.setState({ validationError: GetValidationMessage(this.props.validationType) });
        } else if (this.state.hasError && message) {
          this.setState({ validationError: message });
        } else {
          this.setState({ validationError: false });
        }
      });
    } else if (this.state.validationError !== message && !this.props.validationType) {
      this.setState({ validationError: message });
    }
  };

  componentDidUpdate() {
    if (this.props.runValidation && this.control && this.control.isValid) {
      this.control.checkError();
    }

    if (this.control?.props?.required && this.control?.props?.files) {
      this.control.activeFiles().length > 0
        ? this.setError(false)
        : this.setError(true, i18n.t('This field is required'));
    }
  }

  renderField = () => {
    const pattern = this.props.validationType ? GetValidationPattern(this.props.validationType) : this.props.pattern;

    if (this.props.children) {
      return React.cloneElement(React.Children.only(this.props.children), {
        dataValue: this.props.dataValue,
        setError: this.setError,
        required: this.props.required,
        name: this.props.name,
        isFloatOutside: this.props.isFloatOutside,
        onValueChange: this.props.onValueChange,
        valueChangeDelay: this.props.valueChangeDelay,
        ref: control => (this.control = control),
        onBlur: this.props.onBlur,
        onFocus: this.props.onFocus,
        scrollContainerId: this.props.scrollContainerId,
        disabled: this.props.disabled,
        'aria-describedby': `${this.props.name}-hint`,
      });
    } else {
      return (
        <FormControl
          {...this.props}
          pattern={pattern ?? this.props.pattern}
          ref={control => (this.control = control)}
          setError={this.setError}
          className={this.props.inputClassName}
          aria-describedby={`${this.props.name}-hint`}
        />
      );
    }
  };

  renderLowerText() {
    const { hintText, hintLink, errorMessage } = this.props;
    const { validationError } = this.state;
    let lowerText = hintText || '';
    let lowerTextStyle = 'input-hint';

    const error = errorMessage || validationError;
    if (error) {
      lowerText = error;
      lowerTextStyle = 'input-error';
    }

    if (!lowerText) return null;

    const commonHintProps = {
      className: lowerTextStyle,
      'data-action': lowerTextStyle,
      id: `${this.props.name}-hint`,
      ...(lowerTextStyle === 'input-error' && { role: 'alert', 'aria-live': 'assertive' }),
    };

    return (
      <>
        {typeof lowerText === 'string' ? (
          <span {...commonHintProps} dangerouslySetInnerHTML={{ __html: UnderscoresToTitleCase(lowerText) }} />
        ) : (
          <span {...commonHintProps}>{lowerText}</span>
        )}
        {hintLink && !validationError ? hintLink : null}
      </>
    );
  }

  render() {
    const {
      activeLanguage,
      className: propClassName,
      data,
      dataObjectId,
      dataObjectType,
      dataObjectAttribute,
      dataObjectAction,
      errorMessage,
      isFloatOutside,
      languages,
      multilingual,
      name,
      required,
      srHelpText,
    } = this.props;

    const currentLanguage = languages?.filter(language => Object.keys(language)[0] === activeLanguage) || '';
    const className = formatClassName([
      propClassName,
      { 'has-error is-invalid': this.state.hasError || errorMessage },
      { 'required-form-control': required },
      { 'fg-floatoutside': isFloatOutside },
    ]);

    const caption = multilingual ? `${this.props.caption} (${currentLanguage[0][activeLanguage]})` : this.props.caption;

    const dataId = dataObjectId || (data && data.id && data.id);
    const dataType = dataObjectType || (data && data.type && data.type);
    const dataAttribute = dataObjectAttribute || (data && data.name && data.name);

    React.Children.forEach(this.props.children, child => {
      if (child.type.propTypes?.controlLabelText && caption) {
        console.warn(`
          This component has its own caption capability (controlLabeltext).
          Please move the caption from the FromField wrapper to the wrapped
          component.
        `);
      }
    });

    return (
      <FormGroup
        controlId={name}
        className={className}
        data-field-name={name}
        data-object-id={dataId}
        data-object-type={dataType}
        data-object-attribute={dataAttribute || `${kebabCase(name)}-form-field`}
        data-object-action={dataObjectAction}
        key={name}
      >
        <div className={this.props.wrapperClassName}>
          {caption && (
            <FormLabel
              className={this.props.labelClassName}
              aria-hidden={this.props.hideControlLabelAria}
              id={`${this.props.name}-label`}
            >
              {this.props.searchField || ''} {caption} {srHelpText && <span className="sr-only">{srHelpText}</span>}
            </FormLabel>
          )}
          {this.renderField()}
          {this.props.tooltip ? (
            <InfoTooltip
              {...this.props}
              label={i18n.t('More information about %s', {
                postProcess: 'sprintf',
                sprintf: [this.props.caption],
              })}
            />
          ) : null}
        </div>
        {this.renderLowerText()}
      </FormGroup>
    );
  }
}
